import { trpc } from "@/utils/trpc";
import { useTranslation } from "react-i18next";
import {
  Loader2, Ticket, Clock, TrendingUp, Star, BarChart3,
  AlertTriangle, CheckCircle2, Users, Activity,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from "recharts";

// ---------- palette & label maps ----------

const QUEUE_COLORS = ["#5B2C93", "#7C4DBC", "#9B6FD4", "#0D9488", "#3B82F6", "#EA580C"];

// ---------- shared bits ----------
function Card({ title, icon: Icon, children, className = "" }: any) {
  return (
    <div className={`rounded-xl border border-[#E8E8E8] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ${className}`}>
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[#F0F0F0]">
        {Icon && <Icon className="h-4 w-4 text-[#8B5CF6]" />}
        <h3 className="text-[13px] font-semibold text-[#2C2C2C] uppercase tracking-wide">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-[#E0E0E0] bg-white px-3 py-2 shadow-md">
      {label && <p className="text-xs font-medium text-[#2C2C2C] mb-1">{label}</p>}
      {payload.map((p: any, i: number) => (
        <p key={i} className="text-xs text-[#6B6B6B] flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="font-semibold text-[#2C2C2C]">{p.value}</span> {p.name}
        </p>
      ))}
    </div>
  );
}

function lastNDays(n: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

export default function TicketDashboard() {
  const { t } = useTranslation();
  const { data: stats, isLoading } = trpc.tickets.ticketDashboard.getStats.useQuery();
  const { data: workload = [] } = trpc.tickets.ticketDashboard.getAgentWorkload.useQuery();

  if (isLoading) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" />
      </div>
    );
  }

  // ---------- palette & label maps inside component ----------
  const STATUS_META: Record<string, { label: string; color: string }> = {
    new: { label: t("tickets.statuses.new"), color: "#3B82F6" },
    assigned: { label: t("tickets.statuses.assigned"), color: "#7C4DBC" },
    inProgress: { label: t("tickets.statuses.inProgress"), color: "#D97706" },
    pending: { label: t("tickets.statuses.pending"), color: "#9CA3AF" },
    resolved: { label: t("tickets.statuses.resolved"), color: "#0D9488" },
    closed: { label: t("tickets.statuses.closed"), color: "#16A34A" },
  };

  const PRIORITY_META: Record<string, { label: string; color: string }> = {
    p1: { label: t("tickets.priorities.p1"), color: "#DC2626" },
    p2: { label: t("tickets.priorities.p2"), color: "#EA580C" },
    p3: { label: t("tickets.priorities.p3"), color: "#D97706" },
    p4: { label: t("tickets.priorities.p4"), color: "#3B82F6" },
  };

  // ---------- KPIs ----------
  const kpis = [
    { label: t("tickets.openTickets"), value: stats?.openCount ?? 0, sub: `${stats?.totalCount ?? 0} ${t("tickets.total")}`, icon: Ticket, color: "#5B2C93", bg: "bg-[#F3EDFB]" },
    { label: t("tickets.slaCompliance"), value: `${stats?.slaCompliance ?? 0}%`, sub: t("tickets.resolvedOnTime"), icon: CheckCircle2, color: "#0D9488", bg: "bg-[#E8F9F8]" },
    { label: t("tickets.mttr"), value: `${stats?.mttr ?? 0}h`, sub: t("tickets.meanTimeToResolve"), icon: Clock, color: "#D97706", bg: "bg-[#FFF4E5]" },
    { label: t("tickets.csat"), value: stats?.csatAvg ? `${stats.csatAvg}/5` : "—", sub: t("tickets.avgSatisfaction"), icon: Star, color: "#DC2626", bg: "bg-[#FFE9E9]" },
  ];

  // ---------- queue volume ----------
  const queueData = (stats?.volumeByDept ?? []).map((d: any) => ({
    name: d.queueName ?? t("tickets.unassigned"),
    tickets: Number(d.count),
  }));

  // ---------- status donut ----------
  const statusData = (stats?.statusBreakdown ?? []).map((d: any) => ({
    key: d.status,
    name: STATUS_META[d.status]?.label ?? d.status,
    value: Number(d.count),
    color: STATUS_META[d.status]?.color ?? "#9CA3AF",
  }));
  const statusTotal = statusData.reduce((a: number, b: any) => a + b.value, 0);

  // ---------- priority bars ----------
  const priorityData = (stats?.priorityBreakdown ?? [])
    .map((d: any) => ({
      key: d.priority,
      name: PRIORITY_META[d.priority]?.label ?? d.priority,
      value: Number(d.count),
      color: PRIORITY_META[d.priority]?.color ?? "#9CA3AF",
    }))
    .sort((a: any, b: any) => (a.key > b.key ? 1 : -1));

  // ---------- 14-day trend ----------
  const createdMap = new Map<string, number>();
  (stats?.createdTrend ?? []).forEach((r: any) => createdMap.set(String(r.date).slice(0, 10), Number(r.count)));
  const resolvedMap = new Map<string, number>();
  (stats?.resolvedTrend ?? []).forEach((r: any) => resolvedMap.set(String(r.date).slice(0, 10), Number(r.count)));
  const trendData = lastNDays(14).map((date) => ({
    date,
    label: new Date(date + "T00:00:00").toLocaleDateString(t("i18n.locale", "en-US"), { month: "short", day: "numeric" }),
    Created: createdMap.get(date) ?? 0,
    Resolved: resolvedMap.get(date) ?? 0,
  }));

  const maxWorkload = Math.max(1, ...workload.map((w: any) => Number(w.openCount) || 0));

  return (
    <div className="flex-1 flex flex-col bg-[#FAFAFB]">
      {/* Page header */}
      <div className="px-0 py-4 border-b border-[#E8E8E8] bg-white">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[#F3EDFB] flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-[#5B2C93]" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-[#2C2C2C]">{t("tickets.operationsDashboard")}</h1>
            <p className="text-sm text-[#6B6B6B]">{t("tickets.ticketMetrics")}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 px-0 py-5 space-y-5">
        {/* KPI Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpis.map((kpi) => (
            <div
              key={kpi.label}
              className="relative overflow-hidden rounded-xl border border-[#E8E8E8] bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
            >
              <span className="absolute left-0 top-0 h-full w-1" style={{ background: kpi.color }} />
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-medium text-[#8A8A8A] uppercase tracking-wider">{kpi.label}</p>
                  <p className="mt-1.5 text-3xl font-bold text-[#1A1A1A] leading-none">{kpi.value}</p>
                  <p className="mt-1.5 text-xs text-[#9CA3AF]">{kpi.sub}</p>
                </div>
                <div className={`p-2.5 rounded-lg ${kpi.bg}`}>
                  <kpi.icon className="h-5 w-5" style={{ color: kpi.color }} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trend + Status donut */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card title={t("tickets.ticketTrendLast14Days")} icon={Activity} className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trendData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gCreated" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7C4DBC" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#7C4DBC" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gResolved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0D9488" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} tickLine={false} axisLine={{ stroke: "#E8E8E8" }} interval="preserveStartEnd" minTickGap={20} />
                <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} tickLine={false} axisLine={false} allowDecimals={false} width={38} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="Created" stroke="#7C4DBC" strokeWidth={2.5} fill="url(#gCreated)" dot={false} activeDot={{ r: 4 }} />
                <Area type="monotone" dataKey="Resolved" stroke="#0D9488" strokeWidth={2.5} fill="url(#gResolved)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-6 mt-1">
              <span className="flex items-center gap-2 text-xs text-[#6B6B6B]"><span className="h-2.5 w-2.5 rounded-full bg-[#7C4DBC]" />{t("tickets.created")}</span>
              <span className="flex items-center gap-2 text-xs text-[#6B6B6B]"><span className="h-2.5 w-2.5 rounded-full bg-[#0D9488]" />{t("tickets.resolved")}</span>
            </div>
          </Card>

          <Card title={t("tickets.ticketsByStatus")} icon={Ticket}>
            {statusTotal === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-sm text-[#9CA3AF]">{t("tickets.noTicketData")}</div>
            ) : (
              <>
                <div className="relative">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={62} outerRadius={92} paddingAngle={2} stroke="none">
                        {statusData.map((s: any) => <Cell key={s.key} fill={s.color} />)}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-3xl font-bold text-[#1A1A1A] leading-none">{statusTotal}</span>
                    <span className="text-[11px] text-[#9CA3AF] uppercase tracking-wide mt-1">{t("tickets.totalTickets")}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
                  {statusData.map((s: any) => (
                    <div key={s.key} className="flex items-center gap-2 text-xs">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                      <span className="text-[#6B6B6B] truncate">{s.name}</span>
                      <span className="ml-auto font-semibold text-[#2C2C2C]">{s.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </div>

        {/* Queue volume + Priority */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card title={t("tickets.ticketVolumeByQueue")} icon={BarChart3}>
            {queueData.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-[#9CA3AF]">{t("tickets.noQueueData")}</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={queueData} margin={{ top: 24, right: 8, left: -18, bottom: 0 }} barCategoryGap="35%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6B6B6B" }} tickLine={false} axisLine={{ stroke: "#E8E8E8" }} tickMargin={8} />
                  <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} tickLine={false} axisLine={false} allowDecimals={false} width={38} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "#F5F0FB" }} />
                  <Bar dataKey="tickets" name="tickets" radius={[6, 6, 0, 0]} maxBarSize={56}>
                    {queueData.map((_: any, i: number) => <Cell key={i} fill={QUEUE_COLORS[i % QUEUE_COLORS.length]} />)}
                    <LabelList dataKey="tickets" position="top" fill="#2C2C2C" fontSize={12} fontWeight={600} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card title={t("tickets.ticketsByPriority")} icon={AlertTriangle}>
            {priorityData.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-[#9CA3AF]">{t("tickets.noPriorityData")}</div>
            ) : (
              <div className="flex flex-col justify-center h-[260px] gap-4">
                {priorityData.map((p: any) => {
                  const total = priorityData.reduce((a: number, b: any) => a + b.value, 0) || 1;
                  const pct = Math.round((p.value / total) * 100);
                  return (
                    <div key={p.key}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-[#2C2C2C]">{p.name}</span>
                        <span className="text-xs text-[#9CA3AF]">{p.value} · {pct}%</span>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-[#F0F0F0] overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: p.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Agent Workload */}
        <Card title={t("tickets.agentWorkload")} icon={Users} className="overflow-hidden">
          <div className="-mx-5 -mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-[#F0F0F0] bg-[#FAFAFA]">
                  <th className="text-left px-5 py-2.5 text-[11px] font-medium text-[#8A8A8A] uppercase tracking-wider">{t("tickets.agent")}</th>
                  <th className="text-left px-5 py-2.5 text-[11px] font-medium text-[#8A8A8A] uppercase tracking-wider w-[45%]">{t("tickets.openTicketsLabel")}</th>
                  <th className="text-right px-5 py-2.5 text-[11px] font-medium text-[#8A8A8A] uppercase tracking-wider">{t("tickets.avgAgeHours")}</th>
                </tr>
              </thead>
              <tbody>
                {workload.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-12 text-[#9CA3AF]">{t("tickets.noAssignedTickets")}</td></tr>
                ) : (
                  workload.map((w: any) => {
                    const count = Number(w.openCount) || 0;
                    const pct = Math.round((count / maxWorkload) * 100);
                    const name = w.assigneeName ?? t("tickets.unknown");
                    return (
                      <tr key={w.assigneeId} className="border-b border-[#F4F4F4] hover:bg-[#FBF9FE]">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <span className="h-7 w-7 rounded-full bg-[#F3EDFB] text-[#5B2C93] text-xs font-semibold flex items-center justify-center shrink-0">
                              {name.split(" ").map((n: string) => n[0]).slice(0, 2).join("").toUpperCase()}
                            </span>
                            <span className="font-medium text-[#2C2C2C]">{name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-2 flex-1 max-w-[180px] rounded-full bg-[#F0F0F0] overflow-hidden">
                              <div className="h-full rounded-full bg-[#7C4DBC]" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[#2C2C2C] font-semibold tabular-nums w-6">{count}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right text-[#6B6B6B] tabular-nums">{Math.round(w.avgAge ?? 0)}h</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
